import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useAppSettings } from "@/hooks/useAppSettings";
import { AuthModalProvider } from "@/hooks/useAuthModal";
import AuthModal from "@/components/AuthModal";
import { ThemeProvider } from "next-themes";
import { useEffect, useRef, lazy, Suspense } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import SupportChatWidget from "@/components/SupportChatWidget";
import AvatarPreviewBridge from "@/components/AvatarPreviewBridge";
import WelcomeVoice from "@/components/WelcomeVoice";
import StudentRealtimeNotifications from "@/components/StudentRealtimeNotifications";
import ScrollToTop from "@/components/ScrollToTop";
import AccountLockGate from "@/components/AccountLockGate";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import BootLoader from "@/components/BootLoader";
import { GuideProvider } from "@/hooks/useGuide";
import FloatingGuide from "@/components/guide/FloatingGuide";
import { supabase } from "@/integrations/supabase/client";
import { getUserAccessContext, resolveAuthenticatedHome } from "@/lib/access";
import { AppSettingsProvider } from "@/hooks/useAppSettings";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { reloadForModuleLoadFailure } from "@/lib/moduleRecovery";
import { useLearningSession } from "@/hooks/useLearningSession";

const LearningSessionTracker = () => {
  useLearningSession();
  return null;
};

// Retry wrapper for lazy imports (handles stale cached chunks after deployments)
const lazyRetry = (factory: () => Promise<any>) =>
  lazy(() =>
    factory().catch((err: Error) => {
      if (reloadForModuleLoadFailure()) {
        return new Promise(() => {}); // never resolves, page is reloading
      }
      throw err;
    })
  );

// Eager-load the core onboarding/chat flow to avoid chunk failures on critical routes
import Index from "./pages/Index";
import Chat from "./pages/Chat";
import Onboarding from "./pages/Onboarding";
const Login = lazyRetry(() => import("./pages/Login"));
const Signup = lazyRetry(() => import("./pages/Signup"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const ParentDashboard = lazyRetry(() => import("./pages/ParentDashboard"));
const ParentWeeklyDigests = lazyRetry(() => import("./pages/ParentWeeklyDigests"));
const SubjectDetail = lazyRetry(() => import("./pages/SubjectDetail"));
const Features = lazyRetry(() => import("./pages/Features"));
const Gamification = lazyRetry(() => import("./pages/Gamification"));
const Challenges = lazyRetry(() => import("./pages/Challenges"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const StudentNotions = lazyRetry(() => import("./pages/StudentNotions"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const AdminConsole = lazyRetry(() => import("./pages/AdminConsole"));
const AdminUsers = lazyRetry(() => import("./pages/AdminUsers"));
const AdminRealtime = lazyRetry(() => import("./pages/AdminRealtime"));
const AdminSupport = lazyRetry(() => import("./pages/AdminSupport"));
const AdminPricing = lazyRetry(() => import("./pages/AdminPricing"));
const AdminPremiumTest = lazyRetry(() => import("./pages/AdminPremiumTest"));
const AdminRelances = lazyRetry(() => import("./pages/AdminRelances"));
const AdminParentReminders = lazyRetry(() => import("./pages/AdminParentReminders"));
const AdminPWA = lazyRetry(() => import("./pages/AdminPWA"));
const AdminVoices = lazyRetry(() => import("./pages/AdminVoices"));
const AdminHealthChecks = lazyRetry(() => import("./pages/AdminHealthChecks"));
const NotificationRedirect = lazyRetry(() => import("./pages/NotificationRedirect"));
const Games = lazyRetry(() => import("./pages/Games"));
const Scratch = lazyRetry(() => import("./pages/Scratch"));
const TeacherDashboard = lazyRetry(() => import("./pages/TeacherDashboard"));
const TermsOfService = lazyRetry(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const LearningGuide = lazyRetry(() => import("./pages/LearningGuide"));
const HelpCenter = lazyRetry(() => import("./pages/HelpCenter"));
const Homework = lazyRetry(() => import("./pages/Homework"));
const History = lazyRetry(() => import("./pages/History"));
const ProgressPage = lazyRetry(() => import("./pages/Progress"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const AuthRedirectHandler = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { lastRoute, settingsLoaded } = useAppSettings();
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    if (loading || !user) {
      hasRestoredRef.current = false;
      return;
    }

    const hash = window.location.hash;
    const isOAuthCallback = hash.includes("access_token");
    const currentPath = window.location.pathname;

    // On OAuth callback OR first load, check onboarding status
    if (isOAuthCallback || !hasRestoredRef.current) {
      getUserAccessContext(user.id)
        .then((access) => {
          // Strict client-side guard: only force /onboarding for brand new users
          // arriving via OAuth callback. For all other navigations, respect the
          // current page to avoid intempestive redirects caused by transient
          // profile fetch errors or race conditions.
          if (isOAuthCallback) {
            hasRestoredRef.current = true;
            navigate(resolveAuthenticatedHome(access));
            return;
          }

          // If user landed on /onboarding but has already completed it, push to home.
          if (currentPath === "/onboarding" && access.onboardingCompleted) {
            hasRestoredRef.current = true;
            navigate(resolveAuthenticatedHome(access));
            return;
          }

          // Wait for settings to load from DB before restoring route
          if (!settingsLoaded) return;

          if (lastRoute && lastRoute !== "/" && currentPath === "/") {
            hasRestoredRef.current = true;
            navigate(lastRoute);
          } else {
            hasRestoredRef.current = true;
          }
        })
        .catch(() => {
          // On error, don't redirect — keep user on their current page.
          hasRestoredRef.current = true;
        });
      return;
    }
  }, [user, loading, navigate, lastRoute, settingsLoaded]);

  return null;
};

const RouteTracker = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { setLastRoute } = useAppSettings();
  const saveTimeoutRef = useRef<number>();

  useEffect(() => {
    if (!user) return;
    const path = location.pathname;
    // Don't track auth-related routes
    if (["/login", "/signup", "/reset-password", "/onboarding"].includes(path)) return;

    setLastRoute(path);

    // Debounced save to DB
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = window.setTimeout(async () => {
      await (supabase as any).from("user_app_settings").upsert(
        { user_id: user.id, last_route: path },
        { onConflict: "user_id" }
      );
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [location.pathname, user, setLastRoute]);

  return null;
};

const PresenceTracker = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const lastPingRef = useRef(0);

  useEffect(() => {
    if (loading || !user) return;

    const pingPresence = async (force = false) => {
      if (document.visibilityState !== "visible") return;

      const now = Date.now();
      if (!force && now - lastPingRef.current < 60000) return;

      lastPingRef.current = now;
      await supabase.from("profiles").update({ last_active_at: new Date(now).toISOString() }).eq("id", user.id);
    };

    void pingPresence(true);

    const interval = window.setInterval(() => {
      void pingPresence();
    }, 60000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void pingPresence(true);
      }
    };

    window.addEventListener("focus", handleVisibilityChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleVisibilityChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loading, user, location.pathname]);

  return null;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} forcedTheme={undefined}>
        <TooltipProvider>
          <AuthProvider>
            <AppSettingsProvider>
            <AuthModalProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AuthRedirectHandler />
                <RouteTracker />
                <PresenceTracker />
                <LearningSessionTracker />
                <ScrollToTop />
                <AuthModal />
                <SupportChatWidget />
                <WelcomeVoice />
                <StudentRealtimeNotifications />
                <AvatarPreviewBridge />
                <PWAInstallPrompt />
                <GuideProvider>
                  <FloatingGuide />
                <Suspense fallback={<BootLoader />}>
                <AccountLockGate>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/chat" element={<Chat />} />
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/parent-dashboard" element={<ParentDashboard />} />
                  <Route path="/parent-dashboard/resumes-hebdomadaires" element={<ParentWeeklyDigests />} />
                  <Route path="/subject/:subject" element={<SubjectDetail />} />
                  <Route path="/fonctionnalites" element={<Features />} />
                  <Route path="/gamification" element={<Gamification />} />
                  <Route path="/challenges" element={<Challenges />} />
                  <Route path="/tarifs" element={<Pricing />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/admin" element={<AdminConsole />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/realtime" element={<AdminRealtime />} />
                  <Route path="/admin/support" element={<AdminSupport />} />
                  <Route path="/admin/pricing" element={<AdminPricing />} />
                  <Route path="/admin/premium-test" element={<AdminPremiumTest />} />
                  <Route path="/admin/relances" element={<AdminRelances />} />
                  <Route path="/admin/parent-reminders" element={<AdminParentReminders />} />
                  <Route path="/admin/pwa" element={<AdminPWA />} />
                  <Route path="/admin/voix" element={<AdminVoices />} />
                  <Route path="/admin/health" element={<AdminHealthChecks />} />
                  <Route path="/n/:id" element={<NotificationRedirect />} />
                  <Route path="/games" element={<Games />} />
                  <Route path="/scratch" element={<Scratch />} />
                  <Route path="/teacher-dashboard" element={<TeacherDashboard />} />
                  <Route path="/student-notions" element={<StudentNotions />} />
                  <Route path="/homework/:quizId" element={<Homework />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/progress" element={<ProgressPage />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/conditions" element={<TermsOfService />} />
                  <Route path="/confidentialite" element={<PrivacyPolicy />} />
                  <Route path="/guide-apprentissage" element={<LearningGuide />} />
                  <Route path="/centre-aide" element={<HelpCenter />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </AccountLockGate>
                </Suspense>
                </GuideProvider>
              </BrowserRouter>
            </AuthModalProvider>
            </AppSettingsProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
